import React from 'react'

import { useMutation } from '@apollo/client'
import { DevTool } from '@hookform/devtools'
import { Menu } from '@mui/icons-material'
import { LoadingButton, LoadingButton as MLoadingButton } from '@mui/lab'
import {
  AppBar,
  Box,
  Button,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import MuiAlert from '@mui/material/Alert'
import Decimal from 'decimal.js'
import GoogleMapReact from 'google-map-react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  ControlledCheckbox,
  ControlledSelector,
  ControlledTextField,
  TextInput,
} from 'components/common'
import XeroPurchaseCodesMultipleAutocomplete from 'components/common/custom-queries/XeroCodesMultipleAutocomplete'
import { API_KEY, BRISTOL_COORDINATES } from 'constants/TrackingMap'
import {
  EmailSentStatus,
  EmailTemplateType,
  SmsTemplateType,
  DynamicEmailTemplateType,
  useCreateInvoiceTaskMutation,
  useDeleteInstallationByIdMutation,
  useProcessInvoiceTaskMutation,
  useRefreshExchangeRatesMutation,
  useSendDynamicTemplateEmailMutation,
  useSendTestSmsMutation,
  useSendTestSmsWithTemplateMutation,
  useTestSendingEmailToCustomerMutation,
  useTestSendingEmailToDriverMutation,
  useGenerateTestTrackingHashMutation,
  useGetBookingAddressesLazyQuery,
  useArrivedAtAddressMutation,
  useGetBookingAllocatedDriversLazyQuery,
} from 'generated/graphql'
import {
  SUBMIT_DRIVER_PROVIDED_ETA,
} from 'graphql/mutations/BookingMutations'
import { CREATE_OR_UPDATE_DRIVER_LOCATION } from 'graphql/mutations/DriverMutations'

import { ButtonChangeTheme } from '../navigation'

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DriverPinMarker = (_props: { lat: number; lng: number }) => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: '50%',
      backgroundColor: '#471D7C',
      border: '3px solid #fff',
      boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
      transform: 'translate(-50%, -50%)',
    }}
  />
)

type EmailFormData = {
  type: EmailTemplateType
  bookingId: number
  driverId: number
  bookingAddressId?: number
}

// type GenericEmailFormData = {
//   email: string
//   emailType: string
//   subject: string
//   body: string
//   templateId: string
// }

type SmsFormData = {
  templateType: string
  bookingId?: number
}

export default function Artifacts() {
  const [loading, setLoading] = React.useState(false)
  const [generatedLink, setGeneratedLink] = React.useState<string | null>(null)

  const handleLoadingClick = () => {
    setLoading(true)
    setTimeout(() => setLoading(false), 3000)
  }

  // Testing Toasts
  const types = ['info', 'success', 'warning', 'error', 'default', 'dark']
  let iterator = 0
  const handleToastButton = () => {
    const type = types[iterator % types.length] as any
    toast(type, { type, autoClose: 10000 })
    iterator++
  }
  const handleToastAllButton = () => {
    types.forEach((type: any) => {
      toast(type, { type, autoClose: 10000 })
    })
  }

  const { control, watch, trigger } = useForm({
    shouldUnregister: true,
    defaultValues: {
      x: 0.3,
      y: 0.1,
    },
  })

  const x = Number(watch('x', 0))
  const y = Number(watch('y', 0))

  const decimalX = new Decimal(x)
  const decimalY = new Decimal(y)

  // https://mikemcl.github.io/decimal.js/#prototype-methods

  if (x === 0 && y === 0) {
    trigger()
  }

  const decimalJs = decimalX.minus(decimalY)
  const decimalNative = x - y

  const myAppBar = (appBarColor, titleColor = 'initial') => (
    <AppBar position='static' color={appBarColor}>
      <Toolbar>
        <IconButton
          edge='start'
          sx={{ marginRight: 2 }}
          color='inherit'
          aria-label='menu'
          size='large'>
          <Menu />
        </IconButton>
        <Typography variant='h6' sx={{ flexGrow: 1 }} color={titleColor as any}>
          OnePoint
        </Typography>
        <Typography sx={{ flexGrow: 1 }}>{`${appBarColor}${
          titleColor ? ` with ${titleColor} title` : ''
        }`}</Typography>
        <Button color='inherit'>Login</Button>
      </Toolbar>
    </AppBar>
  )

  // Testing Emails
  const { control: control_email, getValues } = useForm<EmailFormData>({
    shouldUnregister: true,
    defaultValues: {
      type: EmailTemplateType.CustomerBooking,
    },
  })

  // Testing Booking Tracking Hash Creation
  const [trackingBookingId, setTrackingBookingId] = React.useState<string>('')
  const [trackingAddressId, setTrackingAddressId] = React.useState<string>('')
  const [trackingAddresses, setTrackingAddresses] = React.useState<any[]>([])

  const [arrivalBookingId, setArrivalBookingId] = React.useState<string>('')
  const [arrivalAddressId, setArrivalAddressId] = React.useState<string>('')
  const [arrivalDriverId, setArrivalDriverId] = React.useState<string>('')
  const [arrivalAddresses, setArrivalAddresses] = React.useState<any[]>([])
  const [arrivalDrivers, setArrivalDrivers] = React.useState<any[]>([])

  const [getArrivalAddresses, { loading: loadingArrivalAddresses }] =
    useGetBookingAddressesLazyQuery({
      onCompleted: (data) => {
        setArrivalAddresses(data?.booking?.addresses ?? [])
        setArrivalAddressId('')
      },
      onError: () => setArrivalAddresses([]),
    })

  const [getArrivalDrivers, { loading: loadingArrivalDrivers }] =
    useGetBookingAllocatedDriversLazyQuery({
      onCompleted: (data) => {
        setArrivalDrivers(data?.bookingAllocatedDrivers ?? [])
        setArrivalDriverId('')
      },
      onError: () => setArrivalDrivers([]),
    })

  React.useEffect(() => {
    setArrivalAddressId('')
    setArrivalDriverId('')
    if (arrivalBookingId && !isNaN(Number(arrivalBookingId))) {
      getArrivalAddresses({ variables: { bookingId: Number(arrivalBookingId) } })
      getArrivalDrivers({ variables: { bookingId: Number(arrivalBookingId) } })
    } else {
      setArrivalAddresses([])
      setArrivalDrivers([])
    }
  }, [arrivalBookingId, getArrivalAddresses, getArrivalDrivers])

  const [arrivedAtAddress, { loading: submittingArrival }] = useArrivedAtAddressMutation({
    onCompleted: (data) => {
      const addr = data?.arrivedAtAddress
      toast.success(
        `Arrived! Address #${addr?.id} — booking now: ${addr?.booking?.statusLabel ?? addr?.booking?.status}`,
      )
    },
    onError: (err) => toast.error(`Arrival failed: ${err.message}`),
  })

  const handleArrivedAtAddress = () => {
    if (!arrivalBookingId || !arrivalAddressId || !arrivalDriverId) {
      toast.error('Booking ID, Address, and Driver are all required')
      return
    }
    arrivedAtAddress({
      variables: {
        input: {
          bookingId: arrivalBookingId,
          bookingAddressId: arrivalAddressId,
          driverId: arrivalDriverId,
          driverLocationDto: {
            coords: { latitude: 0, longitude: 0 },
            timeStamp: Date.now() / 1000,
          },
        },
      },
    })
  }

  const [getTrackingAddresses, { loading: loadingTrackingAddresses }] =
    useGetBookingAddressesLazyQuery({
      onCompleted: (data) => {
        if (data?.booking?.addresses) {
          setTrackingAddresses(data.booking.addresses)
        }
      },
      onError: () => {
        setTrackingAddresses([])
      },
    })

  React.useEffect(() => {
    setTrackingAddressId('')
    if (trackingBookingId && !isNaN(Number(trackingBookingId))) {
      getTrackingAddresses({ variables: { bookingId: Number(trackingBookingId) } })
    } else {
      setTrackingAddresses([])
    }
  }, [trackingBookingId, getTrackingAddresses])

  const [generateTrackingHash, { loading: generatingTrackingHash }] =
    useGenerateTestTrackingHashMutation({
      onCompleted: (response) => {
        if (response?.generateTestTrackingHash) {
          const hash = response.generateTestTrackingHash.hash
          setGeneratedLink(hash)
          toast.success('Tracking hash generated successfully!')
        } else {
          toast.error('Failed to generate tracking hash')
        }
      },
      onError: (error) => {
        toast.error(`Error: ${error.message}`)
      },
    })

  const handleGenerateTrackingHash = () => {
    if (!trackingBookingId || isNaN(Number(trackingBookingId))) {
      toast.error('Please enter a valid booking ID')
      return
    }

    generateTrackingHash({
      variables: {
        bookingId: Number(trackingBookingId),
        bookingAddressId: trackingAddressId ? Number(trackingAddressId) : undefined,
      },
    })
  }

  // Testing sms notifications
  const { control: controlSms, getValues: getSmsValues } = useForm<{
    text: string
    to: string
    shortenUrls: boolean
  }>({
    defaultValues: {
      to: '+18777804236',
      text: '',
      shortenUrls: false,
    },
  })

  // Testing SMS with template
  const { control: controlSmsTemplate, getValues: getSmsTemplateValues } = useForm<SmsFormData>({
    shouldUnregister: true,
    defaultValues: {
      templateType: SmsTemplateType.EtaUpdate,
    },
  })

  // testing exchange rates
  const [refreshExchangeRates, { loading: refreshingExchangeRates }] =
    useRefreshExchangeRatesMutation({
      onCompleted: () => {
        toast.success('Exchange rates are refreshed')
      },
    })

  const [sendEmailToCustomer, { loading: sendingEmailToCustomer }] =
    useTestSendingEmailToCustomerMutation({
      onCompleted: (response) => {
        if (!response) {
          toast.error('Something went wrong')
          return
        }

        if (response.testSendingEmailToCustomer === EmailSentStatus.Success) {
          toast.success(`Email to Customer sent successfully.`)
        } else {
          toast.warn('Email to Customer not sent, error: ' + response.testSendingEmailToCustomer)
        }
      },
    })

  const [sendEmailToDriver, { loading: sendingEmailToDriver }] =
    useTestSendingEmailToDriverMutation({
      onCompleted: (response) => {
        if (!response) {
          toast.error('Something went wrong')
          return
        }

        if (response.testSendingEmailToDriver === EmailSentStatus.Success) {
          toast.success(`Email to Driver sent successfully.`)
        } else {
          toast.warn('Email to Driver not sent, error: ' + response.testSendingEmailToDriver)
        }
      },
    })

  // Testing dynamic template email
  const {
    control: controlDynamicEmail,
    getValues: getDynamicEmailValues,
    watch: watchDynamicEmail,
  } = useForm<{
    toEmail: string
    emailType: DynamicEmailTemplateType | ''
    bookingId?: string
    addressId?: string
  }>({
    shouldUnregister: true,
    defaultValues: {
      toEmail: '',
      emailType: '',
      bookingId: '',
      addressId: '',
    },
  })

  const dynamicEmailBookingId = watchDynamicEmail('bookingId')
  const [bookingAddresses, setBookingAddresses] = React.useState<any[]>([])

  const [getBookingAddresses, { loading: loadingAddresses }] = useGetBookingAddressesLazyQuery({
    onCompleted: (data) => {
      if (data?.booking?.addresses) {
        setBookingAddresses(data.booking.addresses)
      }
    },
    onError: (error) => {
      toast.error(`Error fetching addresses: ${error.message}`)
      setBookingAddresses([])
    },
  })

  React.useEffect(() => {
    if (dynamicEmailBookingId && !isNaN(Number(dynamicEmailBookingId))) {
      getBookingAddresses({
        variables: {
          bookingId: Number(dynamicEmailBookingId),
        },
      })
    } else {
      setBookingAddresses([])
    }
  }, [dynamicEmailBookingId, getBookingAddresses])

  const [sendDynamicTemplateEmail, { loading: sendingDynamicEmail }] =
    useSendDynamicTemplateEmailMutation({
      onCompleted: (response) => {
        if (response?.sendDynamicTemplateEmail) {
          toast.success('Email queued successfully!')
        } else {
          toast.error('Failed to queue email')
        }
      },
      onError: (error) => {
        toast.error(`Error: ${error.message}`)
      },
    })

  const [sendSms, { loading: sendingSms }] = useSendTestSmsMutation({
    onCompleted: (response) => {
      console.warn('SMS: ', response)
    },
  })

  const [sendSmsWithTemplate, { loading: sendingSmsWithTemplate }] =
    useSendTestSmsWithTemplateMutation({
      onCompleted: (response) => {
        if (response?.sendTestSmsWithTemplate) {
          toast.success(`SMS sent: ${response.sendTestSmsWithTemplate}`)
        } else {
          toast.error('Failed to send SMS')
        }
      },
      onError: (error) => {
        toast.error(`Error: ${error.message}`)
      },
    })

  const [createTask, { loading: creatingTask }] = useCreateInvoiceTaskMutation({
    onCompleted: (response) => {
      if (response.createInvoiceTask) {
        toast.success('task was created')
      } else {
        toast.warning('task exists')
      }
    },
  })

  const [updateTask, { loading: updatingTask }] = useProcessInvoiceTaskMutation({
    onCompleted: (response) => {
      if (response.processTasks) {
        toast.success('invoices was processed')
      } else {
        toast.error('something went wrong')
      }
    },
  })

  const handleSendToCustomer = () => {
    const values = getValues()
    sendEmailToCustomer({
      variables: {
        type: values.type,
        bookingId: +values.bookingId,
        bookingAddressId: Number(values.bookingAddressId),
      },
    })
  }

  const handleSendSmsNotification = () => {
    const values = getSmsValues()

    sendSms({
      variables: {
        ...values,
      },
    })
  }

  const handleSendSmsWithTemplate = () => {
    const values = getSmsTemplateValues()

    if (!values.bookingId) {
      toast.error('Please enter a Booking ID')
      return
    }

    sendSmsWithTemplate({
      variables: {
        bookingId: Number(values.bookingId),
        templateType: values.templateType as any,
      },
    })
  }

  const handleSendToDriver = () => {
    const values = getValues()
    sendEmailToDriver({
      variables: {
        type: values.type,
        bookingId: +values.bookingId,
        driverId: +values.driverId,
      },
    })
  }

  const handleSendDynamicEmail = () => {
    const values = getDynamicEmailValues()

    if (!values.toEmail) {
      toast.error('Email address is required')
      return
    }

    if (!values.emailType) {
      toast.error('Email Type is required')
      return
    }

    if (!values.bookingId) {
      toast.error('Booking ID is required')
      return
    }

    if (!values.addressId) {
      toast.error('Address ID is required')
      return
    }

    sendDynamicTemplateEmail({
      variables: {
        toEmail: values.toEmail,
        emailType: values.emailType as DynamicEmailTemplateType,
        bookingId: parseInt(values.bookingId),
        addressId: parseInt(values.addressId),
      },
    })
  }

  /* Test Azure Notification Hub */
  // const { data: allNotificationHubRegistration } = useAllNotificationHubRegistrationQuery()

  const { control: controlANH, handleSubmit: handleInstallationSubmit } = useForm({
    defaultValues: {
      installationId: '',
    },
  })

  const [deleteById] = useDeleteInstallationByIdMutation()

  const deviceInstallationDelete = (data: any) => {
    deleteById({
      variables: {
        installationId: Number(data.installationId),
      },
    })
  }

  // ── Driver Location section ──────────────────────────────────────────────
  const [driverLocBookingId, setDriverLocBookingId] = React.useState<string>('')
  const [driverLocDriverId, setDriverLocDriverId] = React.useState<string>('')
  const [driverLocDrivers, setDriverLocDrivers] = React.useState<any[]>([])
  const [driverLocCoords, setDriverLocCoords] = React.useState<{ lat: number; lng: number }>(BRISTOL_COORDINATES)

  const [getDriverLocDrivers, { loading: loadingDriverLocDrivers }] =
    useGetBookingAllocatedDriversLazyQuery({
      onCompleted: (data) => {
        setDriverLocDrivers(data?.bookingAllocatedDrivers ?? [])
        setDriverLocDriverId('')
      },
      onError: () => setDriverLocDrivers([]),
    })

  React.useEffect(() => {
    setDriverLocDriverId('')
    setDriverLocDrivers([])
    if (driverLocBookingId && !isNaN(Number(driverLocBookingId))) {
      getDriverLocDrivers({ variables: { bookingId: Number(driverLocBookingId) } })
    }
  }, [driverLocBookingId, getDriverLocDrivers])

  const [createOrUpdateDriverLocation, { loading: submittingDriverLoc }] = useMutation(
    CREATE_OR_UPDATE_DRIVER_LOCATION,
    {
      onCompleted: () => toast.success(`Driver location updated to (${driverLocCoords.lat.toFixed(5)}, ${driverLocCoords.lng.toFixed(5)})`),
      onError: (err) => toast.error(`Driver location failed: ${err.message}`),
    },
  )

  const handleMapClick = ({ lat, lng }: { lat: number; lng: number }) => {
    setDriverLocCoords({ lat, lng })
  }

  const handleSetDriverLocation = () => {
    if (!driverLocDriverId) {
      toast.error('Select a driver first')
      return
    }
    createOrUpdateDriverLocation({
      variables: {
        input: {
          driverId: driverLocDriverId,
          location: {
            coords: {
              latitude: driverLocCoords.lat,
              longitude: driverLocCoords.lng,
            },
            timeStamp: Date.now() / 1000,
          },
        },
      },
    })
  }

  // ── ETA section ──────────────────────────────────────────────────────────
  const [etaBookingId, setEtaBookingId] = React.useState<string>('')
  const [etaDriverId, setEtaDriverId] = React.useState<string>('')
  const [etaAddressId, setEtaAddressId] = React.useState<string>('')
  const [etaDrivers, setEtaDrivers] = React.useState<any[]>([])
  const [etaAddresses, setEtaAddresses] = React.useState<any[]>([])
  const [etaDateTime, setEtaDateTime] = React.useState<string>('')

  const [getEtaDrivers, { loading: loadingEtaDrivers }] =
    useGetBookingAllocatedDriversLazyQuery({
      onCompleted: (data) => {
        setEtaDrivers(data?.bookingAllocatedDrivers ?? [])
        setEtaDriverId('')
      },
      onError: () => setEtaDrivers([]),
    })

  const [getEtaAddresses, { loading: loadingEtaAddresses }] =
    useGetBookingAddressesLazyQuery({
      onCompleted: (data) => {
        setEtaAddresses(data?.booking?.addresses ?? [])
        setEtaAddressId('')
      },
      onError: () => setEtaAddresses([]),
    })

  React.useEffect(() => {
    setEtaDriverId('')
    setEtaAddressId('')
    setEtaDrivers([])
    setEtaAddresses([])
    if (etaBookingId && !isNaN(Number(etaBookingId))) {
      getEtaDrivers({ variables: { bookingId: Number(etaBookingId) } })
      getEtaAddresses({ variables: { bookingId: Number(etaBookingId) } })
    }
  }, [etaBookingId, getEtaDrivers, getEtaAddresses])

  const [submitDriverProvidedEta, { loading: submittingEta }] = useMutation(
    SUBMIT_DRIVER_PROVIDED_ETA,
    {
      onCompleted: (data) => {
        const eta = data?.submitDriverProvidedEta?.eTA
        toast.success(`ETA set: ${eta ? new Date(eta).toLocaleString() : 'updated'}`)
      },
      onError: (err) => toast.error(`ETA failed: ${err.message}`),
    },
  )

  const handleSubmitEta = () => {
    if (!etaDriverId || !etaAddressId) {
      toast.error('Driver and Address are required')
      return
    }
    submitDriverProvidedEta({
      variables: {
        input: {
          driverId: etaDriverId,
          bookingAddressId: etaAddressId,
          updateGeoEtaDto: {
            calculateAutomatically: false,
            eta: etaDateTime ? new Date(etaDateTime).toISOString() : null,
          },
        },
      },
    })
  }

  return (
    <>
      <Container sx={{ py: 4 }}>
        <Typography variant='h5' sx={{ mb: 3 }}>
          Test Sending Emails
        </Typography>
        <Box sx={{ mb: 6 }}>
          <form>
            <Grid container spacing={3}>
              <Grid item>
                <ControlledSelector
                  control={control_email}
                  label='Type'
                  name='type'
                  options={Object.values(EmailTemplateType).map((type) => ({
                    value: type,
                    label: type,
                  }))}
                />
              </Grid>
              <Grid item>
                <ControlledTextField
                  name='bookingId'
                  control={control_email}
                  label='Booking ID'
                  type='number'
                />
              </Grid>
              <Grid item>
                <ControlledTextField
                  name='bookingAddressId'
                  label='Booking address ID'
                  type='number'
                  control={control_email}
                />
              </Grid>
              <Grid item>
                <ControlledTextField
                  name='driverId'
                  control={control_email}
                  label='Driver ID'
                  type='number'
                />
              </Grid>
              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={sendingEmailToCustomer}
                  onClick={handleSendToCustomer}>
                  Send to CUSTOMER
                </LoadingButton>
              </Grid>
              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={sendingEmailToDriver}
                  onClick={handleSendToDriver}>
                  Send to DRIVER
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </Box>

        <Typography variant='h5' sx={{ mb: 3 }}>
          Buttons
        </Typography>

        <Box
          sx={{
            mb: 6,
            '& > *': {
              margin: 1,
            },
          }}>
          <Button variant='contained'>Default</Button>
          <Button variant='contained' color='primary'>
            Primary
          </Button>
          <Button variant='contained' color='secondary'>
            Secondary
          </Button>
          <Button variant='contained' disabled>
            Disabled
          </Button>
          <Button variant='contained' color='primary' href='#contained-buttons'>
            Link
          </Button>
          <LoadingButton
            variant='contained'
            onClick={handleLoadingClick}
            loading={loading}
            style={{ minWidth: 120 }}>
            Loading Button
          </LoadingButton>

          <Button variant='contained' onClick={handleToastButton}>
            One Toast
          </Button>
          <Button variant='contained' onClick={handleToastAllButton}>
            All Toasts
          </Button>

          <ButtonChangeTheme />
        </Box>

        <Typography variant='h6' sx={{ mb: 2 }}>
          Alerts (and colors)
        </Typography>
        <Box sx={{ mb: 6 }}>
          <MuiAlert elevation={6} variant='filled' severity='error'>
            This is an error message!
          </MuiAlert>
          <MuiAlert elevation={6} variant='filled' severity='warning'>
            This is a warning message!
          </MuiAlert>
          <MuiAlert elevation={6} variant='filled' severity='info'>
            This is an information message!
          </MuiAlert>
          <MuiAlert elevation={6} variant='filled' severity='success'>
            This is a success message!
          </MuiAlert>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography variant='h6' sx={{ mb: 3 }}>
            Test Sms notifications
          </Typography>
          <Paper sx={{ p: 3 }}>
            <form>
              <Grid container spacing={3}>
                <Grid item>
                  <ControlledTextField name='to' control={controlSms} label='To' required />
                </Grid>

                <Grid item>
                  <ControlledTextField name='text' control={controlSms} label='text' required />
                </Grid>

                <Grid item>
                  <ControlledCheckbox
                    name='shortenUrls'
                    control={controlSms}
                    label='Shorten URLs'
                  />
                </Grid>

                <Grid item>
                  <LoadingButton
                    variant='contained'
                    color='primary'
                    loading={sendingSms}
                    onClick={handleSendSmsNotification}>
                    Send SMS
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography variant='h6' sx={{ mb: 3 }}>
            Test SMS with Template (Booking)
          </Typography>
          <Paper sx={{ p: 3 }}>
            <form>
              <Grid container spacing={3}>
                <Grid item>
                  <ControlledSelector
                    control={controlSmsTemplate}
                    label='Template Type'
                    name='templateType'
                    options={Object.values(SmsTemplateType).map((type) => ({
                      value: type,
                      label: type,
                    }))}
                  />
                </Grid>

                <Grid item>
                  <ControlledTextField
                    name='bookingId'
                    control={controlSmsTemplate}
                    label='Booking ID'
                    type='number'
                    required
                  />
                </Grid>

                <Grid item>
                  <LoadingButton
                    variant='contained'
                    color='primary'
                    loading={sendingSmsWithTemplate}
                    onClick={handleSendSmsWithTemplate}>
                    Send SMS to Customer
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Paper sx={{ p: 3 }}>
            <form>
              <Grid container spacing={3}>
                <Grid item>
                  <ControlledTextField
                    name='installationId'
                    type='string'
                    control={controlANH}
                    label='Installation Id'
                    required
                  />
                </Grid>
                <Grid item>
                  <Button
                    variant='contained'
                    onClick={handleInstallationSubmit(deviceInstallationDelete)}>
                    DELETE DEVICE INSTALLATION BY ID
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Box sx={{ mb: 6 }}>
          <Typography variant='h6' sx={{ mb: 3 }}>
            <Link href='https://mikemcl.github.io/decimal.js/#prototype-methods'>Decimal JS</Link>
          </Typography>
          <Paper sx={{ p: 3 }}>
            <form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ControlledTextField name='x' control={control} label='x' type='number' />
                </Grid>
                <Grid item xs={12}>
                  <ControlledTextField name='y' control={control} label='y' type='number' />
                </Grid>
                <Grid item>
                  <TextField
                    variant='outlined'
                    value={decimalJs}
                    label='Decimal.js x-y'
                    type='number'
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    variant='outlined'
                    value={decimalNative}
                    label='Native x-y='
                    type='number'
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Typography variant='h6' sx={{ mb: 2 }}>
          Text Field
        </Typography>
        <Box sx={{ margin: 1, mb: 6 }}>
          <TextInput label='Read Only' name='readOnly' value='Test text' readOnly />
        </Box>

        <Typography variant='h5' sx={{ mb: 3 }}>
          Exchange Rates
        </Typography>
        <Box sx={{ mb: 6 }}>
          <MLoadingButton onClick={() => refreshExchangeRates()} loading={refreshingExchangeRates}>
            Refresh Exchange Rates
          </MLoadingButton>
        </Box>

        <Typography variant='h5' sx={{ mb: 3 }}>
          Scheduled Invoice Service
        </Typography>
        <Box sx={{ mb: 6 }}>
          <MLoadingButton onClick={() => createTask()} loading={creatingTask}>
            Create task
          </MLoadingButton>
          <MLoadingButton onClick={() => updateTask()} loading={updatingTask}>
            Process task
          </MLoadingButton>
        </Box>

        <Typography variant='h5' sx={{ mb: 3 }}>
          App Bars
        </Typography>
        <Box
          sx={{
            mb: 6,
            '& > *': {
              marginTop: 2,
            },
          }}>
          {myAppBar('default')}
          {myAppBar('primary')}
          {myAppBar('secondary')}
          {myAppBar('transparent')}
          <Typography variant='h6'>&nbsp;</Typography>
          {myAppBar('default', 'primary')}
          {myAppBar('secondary', 'primary')}
          {myAppBar('transparent', 'primary')}
          <Typography variant='h6'>&nbsp;</Typography>
          {myAppBar('default', 'secondary')}
          {myAppBar('primary', 'secondary')}
          {myAppBar('transparent', 'secondary')}
        </Box>
        <Box sx={{ mt: 2 }}>
          <XeroPurchaseCodesMultipleAutocomplete control={control} />
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3, mb: 3 }}>
          Send Dynamic Template Email
        </Typography>
        <Box sx={{ mb: 6 }}>
          <Paper sx={{ p: 3 }}>
            <form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <ControlledTextField
                    name='toEmail'
                    control={controlDynamicEmail}
                    label='Recipient Email Address'
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <ControlledSelector
                    name='emailType'
                    control={controlDynamicEmail}
                    label='Email Template Type'
                    options={[
                      { value: '', label: 'Select Type' },
                      ...Object.values(DynamicEmailTemplateType).map((type) => ({
                        value: type,
                        label: type,
                      })),
                    ]}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <ControlledTextField
                    name='bookingId'
                    control={controlDynamicEmail}
                    label='Booking ID *'
                    type='number'
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <ControlledSelector
                    name='addressId'
                    control={controlDynamicEmail}
                    label='Booking Address *'
                    options={[
                      { value: '', label: 'Select Address' },
                      ...bookingAddresses.map((addr: any) => ({
                        value: addr.id.toString(),
                        label: `${addr.type} - ${addr.address?.name || 'N/A'} (${
                          addr.address?.postcode || 'N/A'
                        })`,
                      })),
                    ]}
                    disabled={loadingAddresses || bookingAddresses.length === 0}
                    fullWidth
                  />
                </Grid>

                <Grid item>
                  <LoadingButton
                    variant='contained'
                    color='primary'
                    loading={sendingDynamicEmail}
                    onClick={handleSendDynamicEmail}>
                    Send Email
                  </LoadingButton>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3 }}>
          Tracking Links
        </Typography>
        <Box sx={{ mt: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
          <Typography variant='h6' sx={{ mb: 2 }}>
            Generate New Tracking Link
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label='Booking ID'
              variant='outlined'
              size='small'
              type='number'
              value={trackingBookingId}
              onChange={(e) => setTrackingBookingId(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <FormControl size='small' sx={{ minWidth: 300 }}>
              <InputLabel>Address (optional — customer-scoped)</InputLabel>
              <Select
                value={trackingAddressId}
                label='Address (optional — customer-scoped)'
                onChange={(e) => setTrackingAddressId(e.target.value)}
                disabled={loadingTrackingAddresses || trackingAddresses.length === 0}>
                <MenuItem value=''>
                  <em>Booking-level (no address filter)</em>
                </MenuItem>
                {trackingAddresses.map((addr: any) => (
                  <MenuItem key={addr.id} value={addr.id.toString()}>
                    {addr.type} - {addr.address?.name || 'N/A'} ({addr.address?.postcode || 'N/A'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LoadingButton
              variant='contained'
              color='primary'
              loading={generatingTrackingHash}
              onClick={handleGenerateTrackingHash}>
              Generate
            </LoadingButton>
          </Box>

          {generatedLink && (
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Generated Link Hash:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  value={generatedLink}
                  variant='outlined'
                  size='small'
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <Button
                  variant='outlined'
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink)
                    toast.success('Link hash copied to clipboard!')
                  }}>
                  Copy
                </Button>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={() => {
                    const url = `/live-tracking/${generatedLink}`
                    window.open(url, '_blank')
                  }}>
                  Open
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3, mb: 3 }}>
          Driver Arrival (test without app)
        </Typography>
        <Box sx={{ mb: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} sm={3}>
                <TextField
                  label='Booking ID'
                  variant='outlined'
                  size='small'
                  type='number'
                  fullWidth
                  value={arrivalBookingId}
                  onChange={(e) => setArrivalBookingId(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Address</InputLabel>
                  <Select
                    value={arrivalAddressId}
                    label='Address'
                    onChange={(e) => setArrivalAddressId(e.target.value)}
                    disabled={loadingArrivalAddresses || arrivalAddresses.length === 0}>
                    <MenuItem value=''>
                      <em>Select address</em>
                    </MenuItem>
                    {arrivalAddresses.map((addr: any) => (
                      <MenuItem key={addr.id} value={addr.id.toString()}>
                        {addr.type} — {addr.address?.name || 'N/A'} ({addr.address?.postcode || 'N/A'})
                        {addr.arrivedAt ? ' ✓ arrived' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={3}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Driver</InputLabel>
                  <Select
                    value={arrivalDriverId}
                    label='Driver'
                    onChange={(e) => setArrivalDriverId(e.target.value)}
                    disabled={loadingArrivalDrivers || arrivalDrivers.length === 0}>
                    <MenuItem value=''>
                      <em>Select driver</em>
                    </MenuItem>
                    {arrivalDrivers.map((bd: any) => (
                      <MenuItem key={bd.id} value={bd.driver?.id?.toString()}>
                        {bd.driver?.name || `Driver #${bd.driver?.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={submittingArrival}
                  onClick={handleArrivedAtAddress}>
                  Arrived at Address
                </LoadingButton>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3, mb: 1 }}>
          Driver Location (click map to place)
        </Typography>
        <Box sx={{ mb: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems='center' sx={{ mb: 2 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  label='Booking ID'
                  variant='outlined'
                  size='small'
                  type='number'
                  fullWidth
                  value={driverLocBookingId}
                  onChange={(e) => setDriverLocBookingId(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Driver</InputLabel>
                  <Select
                    value={driverLocDriverId}
                    label='Driver'
                    onChange={(e) => setDriverLocDriverId(e.target.value)}
                    disabled={loadingDriverLocDrivers || driverLocDrivers.length === 0}>
                    <MenuItem value=''>
                      <em>Select driver</em>
                    </MenuItem>
                    {driverLocDrivers.map((bd: any) => (
                      <MenuItem key={bd.id} value={bd.driver?.id?.toString()}>
                        {bd.driver?.name || `Driver #${bd.driver?.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label='Lat'
                  variant='outlined'
                  size='small'
                  type='number'
                  fullWidth
                  value={driverLocCoords.lat}
                  onChange={(e) => setDriverLocCoords((c) => ({ ...c, lat: Number(e.target.value) }))}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label='Lng'
                  variant='outlined'
                  size='small'
                  type='number'
                  fullWidth
                  value={driverLocCoords.lng}
                  onChange={(e) => setDriverLocCoords((c) => ({ ...c, lng: Number(e.target.value) }))}
                />
              </Grid>
              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={submittingDriverLoc}
                  disabled={!driverLocDriverId}
                  onClick={handleSetDriverLocation}>
                  Set Location
                </LoadingButton>
              </Grid>
            </Grid>
            <Box
              sx={{ height: 400, width: '100%', borderRadius: 1, overflow: 'hidden', cursor: 'crosshair' }}>
              <GoogleMapReact
                bootstrapURLKeys={{ key: API_KEY }}
                center={driverLocCoords}
                zoom={13}
                onClick={handleMapClick}>
                <DriverPinMarker lat={driverLocCoords.lat} lng={driverLocCoords.lng} />
              </GoogleMapReact>
            </Box>
          </Paper>
        </Box>

        <Typography variant='h5' sx={{ marginTop: 3, mb: 1 }}>
          Set Driver ETA for Address
        </Typography>
        <Box sx={{ mb: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems='center'>
              <Grid item xs={12} sm={3}>
                <TextField
                  label='Booking ID'
                  variant='outlined'
                  size='small'
                  type='number'
                  fullWidth
                  value={etaBookingId}
                  onChange={(e) => setEtaBookingId(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Driver</InputLabel>
                  <Select
                    value={etaDriverId}
                    label='Driver'
                    onChange={(e) => setEtaDriverId(e.target.value)}
                    disabled={loadingEtaDrivers || etaDrivers.length === 0}>
                    <MenuItem value=''>
                      <em>Select driver</em>
                    </MenuItem>
                    {etaDrivers.map((bd: any) => (
                      <MenuItem key={bd.id} value={bd.driver?.id?.toString()}>
                        {bd.driver?.name || `Driver #${bd.driver?.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl size='small' fullWidth>
                  <InputLabel>Address</InputLabel>
                  <Select
                    value={etaAddressId}
                    label='Address'
                    onChange={(e) => setEtaAddressId(e.target.value)}
                    disabled={loadingEtaAddresses || etaAddresses.length === 0}>
                    <MenuItem value=''>
                      <em>Select address</em>
                    </MenuItem>
                    {etaAddresses.map((addr: any) => (
                      <MenuItem key={addr.id} value={addr.id.toString()}>
                        {addr.type} — {addr.address?.name || 'N/A'} ({addr.address?.postcode || 'N/A'})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label='ETA (date/time)'
                  variant='outlined'
                  size='small'
                  type='datetime-local'
                  fullWidth
                  value={etaDateTime}
                  onChange={(e) => setEtaDateTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={submittingEta}
                  disabled={!etaDriverId || !etaAddressId}
                  onClick={handleSubmitEta}>
                  Submit ETA
                </LoadingButton>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>

      <DevTool control={control} />
    </>
  )
}
